import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  TemplateRef,
  Input,
} from '@angular/core';
import { PopUpService } from '../../popupservice.service';
import { CustomerService } from '../../shareModule/services/customer.service';
import { ToastrService } from 'ngx-toastr';
import { select, Store } from '@ngrx/store';
import { ChangeDetectorRef } from '@angular/core';
import { AppState } from 'src/app/shareModule/states/app.state';
import { RoleService } from 'src/app/shareModule/services/roles/role.service';
import { selectUser } from 'src/app/shareModule/states/user-details';
import { User } from 'src/app/shareModule/models/user.model';

interface MapLink {
  id: number;
  title: string;
  url: string;
  icon: string;
  iconType: 'social' | 'custom';
  openInNewTab: boolean;
  filter?: string;
}

@Component({
  selector: 'app-map-links',
  templateUrl: './map-links.component.html',
  styleUrls: ['./map-links.component.scss'],
})
export class MapLinksComponent implements OnInit, OnChanges {
  @ViewChild('addLinkModal', { static: true }) addLinkModal: TemplateRef<any>;
  @Input() filter: string = '';

  mapLinks: MapLink[] = [];
  isLoading = false;
  isAdmin = false;

  newLink: Partial<MapLink> = {
    title: '',
    url: '',
    icon: 'reddit',
    iconType: 'social',
    openInNewTab: true,
  };

  modalRef: any = null;

  iconOptions = [
    { value: 'reddit', label: 'Reddit', icon: 'fab fa-reddit' },
    { value: 'facebook', label: 'Facebook', icon: 'fab fa-facebook' },
    { value: 'instagram', label: 'Instagram', icon: 'fab fa-instagram' },
    { value: 'tiktok', label: 'TikTok', icon: 'fab fa-tiktok' },
    { value: 'hardwarezone', label: 'HardwareZone', icon: 'fas fa-microchip' },
    { value: 'general', label: 'General', icon: 'fas fa-globe' },
  ];

  constructor(
    private popupService: PopUpService,
    private store: Store<AppState>,
    private roleService: RoleService,
    private cdr: ChangeDetectorRef,
    private customerService: CustomerService,
    private toastr: ToastrService,
  ) {}

  user: User;

  ngOnInit(): void {
    this.store.pipe(select(selectUser)).subscribe((user: User) => {
      this.user = user;
      this.roleService.rolesSubject.subscribe((response) => {
        this.isAdmin = response?.userType?.includes('ADMIN');
        this.cdr.detectChanges();
      });
      this.cdr.detectChanges();
    });
    this.loadMapLinks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] && !changes['filter'].firstChange) {
      this.loadMapLinks();
    }
  }

  loadMapLinks(): void {
    this.isLoading = true;
    this.customerService
      .getMapLinks({
        filter: this.filter,
      })
      .subscribe({
        next: (response) => {
          this.mapLinks = response.body || [];
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading map links:', error);
          this.isLoading = false;
          this.toastr.error('Failed to load map links');
          this.cdr.detectChanges();
        },
      });
  }

  openLink(link: MapLink): void {
    if (link.openInNewTab) {
      window.open(link.url, '_blank');
    } else {
      window.location.href = link.url;
    }
  }

  openModal(): void {
    this.newLink = {
      title: '',
      url: '',
      icon: 'reddit',
      iconType: 'social',
      openInNewTab: true,
    };

    this.modalRef = this.popupService.openPopup(this.addLinkModal, {
      modalSize: 'md',
      modalWindowClass: 'add-link-modal',
      modalArialLabelBy: 'modal-basic-title',
      data: this.newLink,
    });
  }

  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  addLink(): void {
    if (this.newLink.title && this.newLink.url) {
      const linkData = {
        title: this.newLink.title!,
        url: this.newLink.url!,
        icon: this.newLink.icon!,
        openInNewTab: this.newLink.openInNewTab!,
        filter: this.filter,
      };

      this.customerService.addMapLink(linkData).subscribe({
        next: (response) => {
          this.toastr.success('Link added successfully');
          this.loadMapLinks(); // Reload the list
          this.closeModal();
        },
        error: (error) => {
          console.error('Error adding map link:', error);
          this.toastr.error('Failed to add link');
        },
      });
    }
  }

  deleteLink(id: number): void {
    this.customerService.deleteMapLink(id).subscribe({
      next: (response) => {
        this.toastr.success('Link deleted successfully');
        this.loadMapLinks(); // Reload the list
      },
      error: (error) => {
        console.error('Error deleting map link:', error);
        this.toastr.error('Failed to delete link');
      },
    });
  }

  getIconClass(icon: string, iconType: string): string {
    const iconOption = this.iconOptions.find((option) => option.value === icon);
    return iconOption ? iconOption.icon : 'fab fa-reddit';
  }
}
